import React, {useCallback, useEffect, useRef, useState} from 'react';
//@ts-ignore
import TelInput from 'react-intl-tel-input';
import {InputAdornment, TextField} from '@material-ui/core';
import Button from '../Button';
import Loading from '../Loading';
import StepRenderer from '../StepRenderer';
import {ReactComponent as FacebookIcon} from '../../assets/image/facebook.svg';

import {useTranslation} from 'react-i18next';

import {AuthActions, Phone, setPhone} from '../../state-management/auth';
import {ErrorHandler, firebase} from '../../externals';

import styles from './SignInForm.module.css';
import 'react-intl-tel-input/dist/main.css';

interface SignInFormProps {
  step: number,
  dispatch: (action: AuthActions) => void
  isLoading: boolean
  phone: Phone
  onChangeConfirmCode: (e: React.ChangeEvent<HTMLInputElement>) => void
  onGoBackTelInput: () => void
  onSignInFacebook: (e : React.MouseEvent<HTMLButtonElement>) => void
  onSignInPhone: (phone: Phone) => void
}


const SignInForm = (props: SignInFormProps) => {

  const {
    step,
    phone,
    dispatch,
    isLoading,
    onSignInPhone,
    onGoBackTelInput,
    onSignInFacebook,
    onChangeConfirmCode,
  } = props;

  const {t} = useTranslation();
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const ref = useRef<Phone>(phone);


  useEffect(() => { // we need to use ref to avoid stale props for the RecaptchaVerifier callback
    ref.current = phone;
  });


  useEffect(() => {
    if (!submitRef.current) return;

    if (submitRef.current) {
      submitRef.current.disabled = !phone.valid || isLoading || !recaptchaLoaded;
    }
  }, [phone.valid, isLoading, recaptchaLoaded]);


  useEffect(() => {
    if (step !== 0) return;

    void async function () {
      try {
        setRecaptchaLoaded(false);
        window.recaptchaVerifier = new firebase.auth!.RecaptchaVerifier('sign-in-btn', {
          size: 'invisible',
          callback: () => {
            onSignInPhone(ref.current);
          },
        });

        window.recaptchaWidgetId = await window.recaptchaVerifier.render();

        setRecaptchaLoaded(true);
      } catch (e) {
        ErrorHandler.report(`Error in render recaptcha, ${e.message}`)
      }
    }();

    return () => {
      window.recaptchaVerifier && window.recaptchaVerifier.clear();
    }
  }, [step, dispatch, onSignInPhone]);

  const onFormSubmit = useCallback((e) => {
    e.preventDefault();

    onSignInPhone(ref.current);
  }, [onSignInPhone]);


  return (
    <StepRenderer step={step}>
      <form onSubmit={onFormSubmit}>
        <TelInput
          inputClassName={styles.input}
          onlyCountries={['vn']}
          preferredCountries={[]}
          placeholder={t('enterYourPhoneNumber')}
          onPhoneNumberChange={(valid: boolean, phoneString: string, country: any) => {
            dispatch(setPhone({valid, number: phoneString, code: country.dialCode}));
          }}
          value={phone.number}
          autoPlaceholder={false}
          defaultCountry="vn"
          autoFocus
        />
        <Button
          ref={submitRef}
          id="sign-in-btn"
          className={styles.button}
          loading={isLoading}
        >
          {t('signIn')}
        </Button>
        <div className={styles.socialContainer}>
          <Button
            className={styles.socialButton}
            onClick={onSignInFacebook}
          >
            <FacebookIcon/>
          </Button>
        </div>
      </form>
      <>
        <TextField
          fullWidth
          autoFocus
          type="text"
          variant="outlined"
          placeholder={t('enterConfirmationCode')}
          onChange={onChangeConfirmCode}
          style={{marginBottom: 24}}
          InputProps={{
            startAdornment: isLoading &&
              <InputAdornment position="start">
                <Loading size="30px"/>
              </InputAdornment>,
          }}
        />
        <Button
          className={styles.button}
          onClick={onGoBackTelInput}
          disabled={isLoading}
        >
          {t('goBack')}
        </Button>
      </>
    </StepRenderer>
  );
};

export default SignInForm;
